import type {
  FormManagementQuestionChartKind,
  FormManagementQuestionChartNumericSummary,
  FormManagementQuestionChartSample,
  FormManagementQuestionChartSegment,
  FormManagementQuestionChartStack,
} from '@/contracts/form-management'
import { type QuestionType, isLayoutQuestionType } from '@/types/form'

export const ANALYTICS_SMALL_SAMPLE_THRESHOLD = 5

export type AnalyticsResponseStatus = 'partial' | 'complete' | 'draft' | 'abandoned'

export type AnalyticsResponseInput = {
  id: string
  status: AnalyticsResponseStatus
  startedAt?: string | null
  submittedAt?: string | null
  completedAt?: string | null
  metadata?: Record<string, unknown> | null
}

export type AnalyticsAnswerInput = {
  responseId: string
  questionId: string
  value: unknown
  valid?: boolean
}

export type AnalyticsQuestionChoice = {
  label: string
  value: string
}

export type AnalyticsQuestionInput = {
  id: string
  title: string
  type: QuestionType
  choices?: AnalyticsQuestionChoice[]
  settings?: Record<string, unknown>
  fieldKey?: string
}

export type AnalyticsResponseMetrics = {
  starts: number
  completions: number
  completionRate: number
  eligibleResponseIds: string[]
}

export type AnalyticsSuppression = {
  suppressed: boolean
  reason?: 'small_sample'
  threshold: number
  sampleSize: number
}

export type AnalyticsQuestionChart = {
  questionId: string
  questionTitle: string
  questionType: QuestionType
  chartKind: FormManagementQuestionChartKind
  totalAnswers: number
  skipped: number
  invalid: number
  completionRate: number
  summary: string
  segments: Array<FormManagementQuestionChartSegment & { suppressed?: boolean }>
  stacks?: FormManagementQuestionChartStack[]
  samples?: FormManagementQuestionChartSample[]
  numericSummary?: FormManagementQuestionChartNumericSummary
  npsScore?: number
  suppression: AnalyticsSuppression
}

export type AggregateQuestionAnalyticsInput = {
  question: AnalyticsQuestionInput
  responses: AnalyticsResponseInput[]
  answers: AnalyticsAnswerInput[]
  smallSampleThreshold?: number
}

export type AnalyticsScope = 'all' | 'team' | 'own' | 'none'

export type AnalyticsRole =
  | 'super_admin'
  | 'admin'
  | 'sales_manager'
  | 'sales'
  | 'sales_assistant'
  | string

export type AnalyticsScopeResolution = {
  allowed: boolean
  effectiveScope: AnalyticsScope
  narrowed: boolean
  reason?: 'analytics_scope_denied'
}

export function calculateResponseMetrics(
  responses: readonly AnalyticsResponseInput[],
): AnalyticsResponseMetrics {
  const eligibleResponses = responses.filter(isEligibleAnalyticsResponse)
  const starts = eligibleResponses.filter(
    (response) => response.status === 'partial' || response.status === 'complete',
  ).length
  const completions = eligibleResponses.filter((response) => response.status === 'complete').length

  return {
    starts,
    completions,
    completionRate: starts === 0 ? 0 : roundPercent((completions / starts) * 100),
    eligibleResponseIds: eligibleResponses.map((response) => response.id),
  }
}

export function aggregateQuestionAnalytics({
  question,
  responses,
  answers,
  smallSampleThreshold = ANALYTICS_SMALL_SAMPLE_THRESHOLD,
}: AggregateQuestionAnalyticsInput): AnalyticsQuestionChart {
  const eligibleResponseIds = new Set(
    responses.filter(isEligibleAnalyticsResponse).map(({ id }) => id),
  )
  const questionAnswers = answers.filter(
    (answer) => answer.questionId === question.id && eligibleResponseIds.has(answer.responseId),
  )
  const nonBlankAnswers = questionAnswers.filter((answer) => !isBlankValue(answer.value))
  const invalidAnswers = nonBlankAnswers.filter((answer) => isInvalidAnswer(question.type, answer))
  const validAnswers = nonBlankAnswers.filter((answer) => !isInvalidAnswer(question.type, answer))
  const responseCount = eligibleResponseIds.size
  const totalAnswers = validAnswers.length
  const skipped = Math.max(0, responseCount - nonBlankAnswers.length)
  const chartKind = getAnalyticsQuestionChartKind(question.type)
  const suppression =
    chartKind === 'layout_block'
      ? { suppressed: false, threshold: smallSampleThreshold, sampleSize: 0 }
      : createSuppression(totalAnswers, smallSampleThreshold)

  let partial: Pick<
    AnalyticsQuestionChart,
    'npsScore' | 'numericSummary' | 'samples' | 'segments' | 'stacks' | 'summary'
  >

  if (chartKind === 'layout_block') {
    partial = {
      summary: '版面區塊不產生答案統計。',
      segments: [],
      samples: [],
    }
  } else if (question.type === 'CHECKBOXES') {
    partial = aggregateCheckboxQuestion(question, validAnswers)
  } else if (question.type === 'NPS') {
    partial = aggregateNpsQuestion(validAnswers)
  } else if (question.type === 'MATRIX') {
    partial = aggregateMatrixQuestion(question, validAnswers)
  } else if (question.type === 'RANKING') {
    partial = aggregateRankingQuestion(question, validAnswers)
  } else if (isPiiQuestionType(question.type) || isTextQuestionType(question.type)) {
    partial = aggregateRedactedSampleQuestion(
      question,
      validAnswers,
      invalidAnswers.length,
      skipped,
    )
  } else if (isNumericQuestionType(question.type)) {
    partial = aggregateNumericQuestion(question, validAnswers)
  } else {
    partial = aggregateChoiceQuestion(question, validAnswers)
  }

  const segments = suppression.suppressed
    ? partial.segments.map((segment) => ({ ...segment, suppressed: true }))
    : partial.segments

  return {
    questionId: question.id,
    questionTitle: question.title,
    questionType: question.type,
    chartKind,
    totalAnswers,
    skipped,
    invalid: invalidAnswers.length,
    completionRate: responseCount === 0 ? 0 : roundPercent((totalAnswers / responseCount) * 100),
    ...partial,
    segments,
    summary: suppression.suppressed ? '樣本數不足，僅供參考。' : partial.summary,
    suppression,
  }
}

export function redactAnalyticsValue(
  questionType: QuestionType,
  value: unknown,
  options: { fieldKey?: string } = {},
): string {
  const text = stringifyAnswerValue(value)

  if (options.fieldKey === 'tax_id' || options.fieldKey === 'business_tax_id') {
    return redactDigits(text, 2, 3)
  }

  if (questionType === 'EMAIL') {
    const [localPart, domain] = text.split('@')
    if (!localPart || !domain) return 'email 已遮蔽'
    return `${localPart.slice(0, 1)}***@${domain}`
  }

  if (
    questionType === 'PHONE_NUMBER' ||
    questionType === 'TAIWAN_MOBILE' ||
    questionType === 'TAIWAN_LANDLINE'
  ) {
    return redactDigits(text, 4, 3)
  }

  if (questionType === 'TAIWAN_ADDRESS') {
    return redactAddress(value)
  }

  if (isTextQuestionType(questionType)) return '文字已遮蔽'
  return text
}

export function resolveAnalyticsScope({
  role,
  requestedScope = 'all',
}: {
  role: AnalyticsRole
  requestedScope?: Exclude<AnalyticsScope, 'none'>
}): AnalyticsScopeResolution {
  const maximumScope = getMaximumScopeForRole(role)
  if (maximumScope === 'none') {
    return {
      allowed: false,
      effectiveScope: 'none',
      narrowed: false,
      reason: 'analytics_scope_denied',
    }
  }

  const requestedRank = scopeRank(requestedScope)
  const maximumRank = scopeRank(maximumScope)
  if (requestedRank <= maximumRank) {
    return {
      allowed: true,
      effectiveScope: requestedScope,
      narrowed: false,
    }
  }

  return {
    allowed: true,
    effectiveScope: maximumScope,
    narrowed: true,
  }
}

function isEligibleAnalyticsResponse(response: AnalyticsResponseInput) {
  if (response.status !== 'partial' && response.status !== 'complete') return false
  const metadata = response.metadata ?? {}
  const source = typeof metadata.source === 'string' ? metadata.source : undefined

  return !(
    metadata.analyticsExcluded === true ||
    metadata.preview === true ||
    metadata.test === true ||
    metadata.demo === true ||
    metadata.synthetic === true ||
    source === 'preview' ||
    source === 'test' ||
    source === 'demo' ||
    source === 'synthetic' ||
    source === 'smoke'
  )
}

function getAnalyticsQuestionChartKind(
  questionType: QuestionType,
): FormManagementQuestionChartKind {
  if (isLayoutQuestionType(questionType)) return 'layout_block'
  if (questionType === 'CHECKBOXES') return 'stacked_bar'
  if (questionType === 'RANKING') return 'ranking'
  if (questionType === 'MATRIX') return 'matrix'
  if (questionType === 'FILE_UPLOAD' || questionType === 'SIGNATURE') return 'file_summary'
  if (questionType === 'DATE' || questionType === 'TIME') return 'timeline'
  if (questionType === 'NUMBER' || questionType === 'TOTAL') return 'numeric_summary'
  if (['OPINION_SCALE', 'RATING', 'NPS', 'NUMBER_SLIDER'].includes(questionType)) {
    return 'histogram'
  }
  if (isTextQuestionType(questionType)) return 'text_cloud'
  if (isPiiQuestionType(questionType) || questionType === 'WEBSITE') return 'sample_list'
  return 'donut'
}

function aggregateChoiceQuestion(
  question: AnalyticsQuestionInput,
  answers: readonly AnalyticsAnswerInput[],
) {
  const choices = getChoiceDefinitions(question)
  const counts = new Map(choices.map((choice) => [choice.label, 0]))
  let other = 0

  for (const answer of answers) {
    const label = resolveChoiceLabel(question, answer.value)
    if (label && counts.has(label)) {
      counts.set(label, (counts.get(label) ?? 0) + 1)
    } else {
      other += 1
    }
  }

  const segments = makeSegmentsFromCounts(counts, answers.length)
  if (other > 0) {
    segments.push({
      id: 'other',
      label: '其他',
      value: other,
      percent: roundPercent((other / Math.max(answers.length, 1)) * 100),
    })
  }

  return {
    summary: '選項題以回答數與百分比呈現。',
    segments,
  }
}

function aggregateCheckboxQuestion(
  question: AnalyticsQuestionInput,
  answers: readonly AnalyticsAnswerInput[],
) {
  const choices = getChoiceDefinitions(question)
  const counts = new Map(choices.map((choice) => [choice.label, 0]))

  for (const answer of answers) {
    const values = Array.isArray(answer.value) ? answer.value : [answer.value]
    for (const value of values) {
      const label = resolveChoiceLabel(question, value)
      if (label && counts.has(label)) counts.set(label, (counts.get(label) ?? 0) + 1)
    }
  }

  return {
    summary: '多選題以每個選項被選次數與回答占比呈現。',
    segments: makeSegmentsFromCounts(counts, answers.length),
  }
}

function aggregateNpsQuestion(answers: readonly AnalyticsAnswerInput[]) {
  const numericValues = answers.map((answer) => Number(answer.value)).filter(Number.isFinite)
  const detractors = numericValues.filter((value) => value >= 0 && value <= 6).length
  const passives = numericValues.filter((value) => value >= 7 && value <= 8).length
  const promoters = numericValues.filter((value) => value >= 9 && value <= 10).length
  const total = Math.max(numericValues.length, 1)

  return {
    summary: 'NPS 以批評者、被動者與推薦者區間呈現。',
    segments: [
      makeSegment('0-6 批評者', detractors, total),
      makeSegment('7-8 被動者', passives, total),
      makeSegment('9-10 推薦者', promoters, total),
    ],
    numericSummary: summarizeNumbers(numericValues, '分'),
    npsScore: Math.round(((promoters - detractors) / total) * 100),
  }
}

function aggregateMatrixQuestion(
  question: AnalyticsQuestionInput,
  answers: readonly AnalyticsAnswerInput[],
) {
  const rows = getStringListSetting(question, 'rows', ['列 1'])
  const columns = getStringListSetting(question, 'columns', ['選項 1'])

  return {
    summary: `${rows.length} 列 x ${columns.length} 欄的矩陣列統計。`,
    segments: [],
    stacks: rows.map((row, rowIndex) => {
      const counts = new Map(columns.map((column) => [column, 0]))
      for (const answer of answers) {
        const value = isPlainRecord(answer.value) ? answer.value[row] : undefined
        if (typeof value === 'string' && counts.has(value)) {
          counts.set(value, (counts.get(value) ?? 0) + 1)
        }
      }

      return {
        id: `matrix-row-${rowIndex}`,
        label: row,
        total: Array.from(counts.values()).reduce((sum, count) => sum + count, 0),
        segments: makeSegmentsFromCounts(counts, answers.length),
      }
    }),
  }
}

function aggregateRankingQuestion(
  question: AnalyticsQuestionInput,
  answers: readonly AnalyticsAnswerInput[],
) {
  const choices = getChoiceDefinitions(question)
  const rankTotals = new Map(choices.map((choice) => [choice.label, 0]))
  const rankCounts = new Map(choices.map((choice) => [choice.label, 0]))

  for (const answer of answers) {
    if (!Array.isArray(answer.value)) continue
    answer.value.forEach((value, index) => {
      const label = resolveChoiceLabel(question, value)
      if (!label || !rankTotals.has(label)) return
      rankTotals.set(label, (rankTotals.get(label) ?? 0) + index + 1)
      rankCounts.set(label, (rankCounts.get(label) ?? 0) + 1)
    })
  }

  return {
    summary: '排序題以平均名次呈現。',
    segments: choices.map((choice, index) => {
      const count = rankCounts.get(choice.label) ?? 0
      const averageRank = count === 0 ? 0 : (rankTotals.get(choice.label) ?? 0) / count
      return {
        id: `ranking-${index}`,
        label: choice.label,
        value: count,
        percent: roundPercent((count / Math.max(answers.length, 1)) * 100),
        helper: `平均第 ${formatRank(averageRank)} 名`,
      }
    }),
  }
}

function aggregateRedactedSampleQuestion(
  question: AnalyticsQuestionInput,
  answers: readonly AnalyticsAnswerInput[],
  invalid: number,
  skipped: number,
) {
  const valid = answers.length
  const total = Math.max(valid + invalid + skipped, 1)

  return {
    summary: '個資與文字題只顯示格式摘要與遮蔽樣本。',
    segments: [
      makeSegment('格式有效', valid, total),
      makeSegment('格式無效', invalid, total),
      makeSegment('未填寫', skipped, total),
    ],
    samples: answers.slice(0, 5).map((answer, index) => ({
      id: `sample-${index}`,
      label: '遮蔽樣本',
      value: redactAnalyticsValue(question.type, answer.value, { fieldKey: question.fieldKey }),
    })),
  }
}

function aggregateNumericQuestion(
  question: AnalyticsQuestionInput,
  answers: readonly AnalyticsAnswerInput[],
) {
  const numericValues = answers.map((answer) => Number(answer.value)).filter(Number.isFinite)
  const unit =
    typeof question.settings?.numberUnit === 'string'
      ? question.settings.numberUnit
      : typeof question.settings?.totalUnit === 'string'
        ? question.settings.totalUnit
        : undefined

  return {
    summary: '數字題以最小值、最大值、平均與中位數呈現。',
    segments: buildNumericBuckets(numericValues),
    numericSummary: summarizeNumbers(numericValues, unit),
  }
}

function createSuppression(sampleSize: number, threshold: number): AnalyticsSuppression {
  if (sampleSize < threshold) {
    return {
      suppressed: true,
      reason: 'small_sample',
      threshold,
      sampleSize,
    }
  }

  return {
    suppressed: false,
    threshold,
    sampleSize,
  }
}

function makeSegmentsFromCounts(
  counts: ReadonlyMap<string, number>,
  denominator: number,
): FormManagementQuestionChartSegment[] {
  const safeDenominator = Math.max(denominator, 1)
  return Array.from(counts.entries()).map(([label, value], index) => ({
    id: `${slugify(label)}-${index}`,
    label,
    value,
    percent: roundPercent((value / safeDenominator) * 100),
  }))
}

function makeSegment(
  label: string,
  value: number,
  denominator: number,
): FormManagementQuestionChartSegment {
  return {
    id: slugify(label),
    label,
    value,
    percent: roundPercent((value / Math.max(denominator, 1)) * 100),
  }
}

function summarizeNumbers(
  values: readonly number[],
  unit?: string,
): FormManagementQuestionChartNumericSummary {
  if (values.length === 0) {
    return {
      min: 0,
      max: 0,
      average: 0,
      median: 0,
      unit,
    }
  }

  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  const median =
    sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle]
  const average = values.reduce((sum, value) => sum + value, 0) / values.length

  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    average: roundNumber(average),
    median: roundNumber(median),
    unit,
  }
}

function buildNumericBuckets(values: readonly number[]): FormManagementQuestionChartSegment[] {
  if (values.length === 0) return []
  const summary = summarizeNumbers(values)
  if (summary.min === summary.max)
    return [makeSegment(String(summary.min), values.length, values.length)]

  const bucketCount = Math.min(5, values.length)
  const step = (summary.max - summary.min) / bucketCount
  const counts = new Map<string, number>()

  for (let index = 0; index < bucketCount; index += 1) {
    const from = roundNumber(summary.min + step * index)
    const to = roundNumber(
      index === bucketCount - 1 ? summary.max : summary.min + step * (index + 1),
    )
    counts.set(`${from}-${to}`, 0)
  }

  for (const value of values) {
    const index = Math.min(bucketCount - 1, Math.floor((value - summary.min) / Math.max(step, 1)))
    const label = Array.from(counts.keys())[index]
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }

  return makeSegmentsFromCounts(counts, values.length)
}

function getChoiceDefinitions(question: AnalyticsQuestionInput): AnalyticsQuestionChoice[] {
  if (question.choices && question.choices.length > 0) return question.choices
  if (question.type === 'YES_NO') {
    return [
      { label: '是', value: 'yes' },
      { label: '否', value: 'no' },
    ]
  }
  return []
}

function resolveChoiceLabel(question: AnalyticsQuestionInput, value: unknown): string | undefined {
  const text = stringifyAnswerValue(value)
  const choice = getChoiceDefinitions(question).find(
    (item) => item.value === text || item.label === text,
  )
  return choice?.label
}

function isInvalidAnswer(questionType: QuestionType, answer: AnalyticsAnswerInput) {
  if (answer.valid === false) return true
  if (
    questionType === 'NPS' ||
    questionType === 'RATING' ||
    questionType === 'OPINION_SCALE' ||
    questionType === 'NUMBER' ||
    questionType === 'NUMBER_SLIDER' ||
    questionType === 'TOTAL'
  ) {
    return !Number.isFinite(Number(answer.value))
  }
  return false
}

function isBlankValue(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  if (isPlainRecord(value)) return Object.keys(value).length === 0
  return false
}

function isPiiQuestionType(questionType: QuestionType) {
  return ['EMAIL', 'PHONE_NUMBER', 'TAIWAN_MOBILE', 'TAIWAN_LANDLINE', 'TAIWAN_ADDRESS'].includes(
    questionType,
  )
}

function isTextQuestionType(questionType: QuestionType) {
  return questionType === 'SHORT_TEXT' || questionType === 'LONG_TEXT'
}

function isNumericQuestionType(questionType: QuestionType) {
  return ['NUMBER', 'TOTAL', 'NUMBER_SLIDER', 'OPINION_SCALE', 'RATING'].includes(questionType)
}

function getStringListSetting(
  question: AnalyticsQuestionInput,
  key: 'rows' | 'columns',
  fallback: string[],
) {
  const value = question.settings?.[key]
  return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : fallback
}

function stringifyAnswerValue(value: unknown) {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function redactDigits(value: string, leading: number, trailing: number) {
  const digits = value.replace(/\D/g, '')
  if (digits.length <= leading + trailing) return '*'.repeat(Math.max(digits.length, 3))
  return `${digits.slice(0, leading)}***${digits.slice(-trailing)}`
}

function redactAddress(value: unknown) {
  if (!isPlainRecord(value)) return '地址已遮蔽'
  const city = typeof value.city === 'string' ? value.city.trim() : ''
  const district = typeof value.district === 'string' ? value.district.trim() : ''
  return [city, district].filter(Boolean).join(' ') || '地址已遮蔽'
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getMaximumScopeForRole(role: AnalyticsRole): AnalyticsScope {
  if (role === 'super_admin' || role === 'admin') return 'all'
  if (role === 'sales_manager' || role === 'sales_assistant') return 'team'
  if (role === 'sales') return 'own'
  return 'none'
}

function scopeRank(scope: AnalyticsScope) {
  if (scope === 'all') return 3
  if (scope === 'team') return 2
  if (scope === 'own') return 1
  return 0
}

function formatRank(value: number) {
  if (value === 0) return '無資料'
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

function roundPercent(value: number) {
  return Math.round(value)
}

function roundNumber(value: number) {
  return Math.round(value * 100) / 100
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\u4e00-\u9fa5-]/g, '')
}
